import { IS_PREVIEW } from '@/utils/env';
import { pushToElevarDataLayer } from './utils';
import { ElevarSubscribeEvent } from './types';

/**
 * Elevar newsletter subscription tracking.
 * Call this function when a user subscribes to newsletter.
 * 
 * Note: Elevar's script automatically handles newsletter signups for certain providers 
 * (Klaviyo, Postscript, Attentive), but due to headless stores being custom, 
 * you may need to manually track these events.
 * 
 * To test if automatic tracking works:
 * 1. Perform newsletter signup on your site
 * 2. Check if window.ElevarDataLayer contains item with event: 'dl_subscribe'
 * 3. If not found, use this manual tracking
 */

/**
 * Track email subscription
 */
export const handleElevarEmailSubscribe = (email: string, phone?: string) => {
  if (IS_PREVIEW) return;

  const event: ElevarSubscribeEvent = {
    event: 'dl_subscribe',
    lead_type: 'email',
    user_properties: {
      customer_email: email,
      ...(phone && { customer_phone: phone })
    }
  };

  pushToElevarDataLayer(event);
};

/**
 * Track phone subscription
 */
export const handleElevarPhoneSubscribe = (phone: string, email?: string) => {
  if (IS_PREVIEW) return;

  const event: ElevarSubscribeEvent = {
    event: 'dl_subscribe',
    lead_type: 'phone',
    user_properties: {
      customer_phone: phone,
      ...(email && { customer_email: email })
    }
  };

  pushToElevarDataLayer(event);
};

/**
 * Track combined email and phone subscription
 * This will fire two events as per Elevar documentation
 */
export const handleElevarCombinedSubscribe = (email: string, phone: string) => {
  if (IS_PREVIEW) return;

  // Fire email event
  handleElevarEmailSubscribe(email, phone);
  
  // Fire phone event
  handleElevarPhoneSubscribe(phone, email);
};

/**
 * Generic subscribe handler that determines the type based on provided data
 */
export const handleElevarSubscribe = (data: { email?: string; phone?: string }) => {
  if (IS_PREVIEW) return;

  const { email, phone } = data;

  if (email && phone) {
    // Both provided - fire both events
    handleElevarCombinedSubscribe(email, phone);
  } else if (email) {
    // Only email provided
    handleElevarEmailSubscribe(email);
  } else if (phone) {
    // Only phone provided
    handleElevarPhoneSubscribe(phone);
  } else {
    console.warn('[Elevar] Subscribe event called without email or phone');
  }
};